import InfoIcon from '@mui/icons-material/Info';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Divider,
    FormControl,
    IconButton,
    Stack,
    type SxProps,
    TextField,
    type Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import AutoCompleteMultiSelect from '@/components/AutoCompleteMultiselect';
import { InfoSolid } from '@/components/Icons/SystemIcons';
import LexoSwitch from '@/components/LexoSwitch';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useChangedFlag } from '@/hooks/util';
import { useUsers } from '@/queries/users';
import useQuoteIntakeSetupStore from '@/stores/useQuoteIntakeSetupStore';
import { convertToHtml } from '@/utils';
import DynamicQuoteStructure from './DynamicQuoteStructure';

const formItemContainerStyle: SxProps<Theme> = {
    display: 'flex',
    position: 'relative',
};

export default function QuoteEmailTemplate() {
    const theme = useTheme();

    const quoteEmailSectionsStyle: SxProps<Theme> = {
        borderRadius: '8px',
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        backgroundColor: 'neutral.white',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        height: 'fit-content',
        flex: '1 1 50%',
    };

    const [formData, availableCcContacts, errorData, setFormData, setErrorData, activeQuote] =
        useQuoteIntakeSetupStore((state) => [
            state.formData,
            state.availableCcContacts,
            state.errorData,
            state.setFormData,
            state.setErrorData,
            state.activeQuote,
        ]);
    const { activeLawFirmId } = useLawFirmContext();
    const lawFirmId = activeQuote.lawFirmId ?? activeLawFirmId;

    const hasSubjectChanged = useChangedFlag(formData.step2.subject);
    const hasBodyBeforePricingChanged = useChangedFlag(formData.step2.bodyBeforePricing);
    const hasBodyAfterPricingChanged = useChangedFlag(formData.step2.bodyAfterPricing);
    const hasSignatureChanged = useChangedFlag(formData.step2.signatureOfEmail);

    const [showTip, setShowTip] = useState(true);
    const [setCcContacts] = useQuoteIntakeSetupStore((state) => [
        state.setCcContact,
        state.fetchQuoteTemplateDataOnEveryStep,
    ]);
    const { data: users = [] } = useUsers({ lawFirmId });

    useEffect(() => {
        const filteredCcContacts = users
            .filter((user) => user.userId !== formData.step0.assignedLawyer)
            .map((user) => user.email);
        setCcContacts(filteredCcContacts);
    }, [formData.step0.assignedLawyer, setCcContacts, users]);

    const handleStateChange = (event: any, key: string) => {
        setErrorData({
            ...errorData,
            step2: {
                quoteEmailTemplateError: {
                    emailSubject: undefined,
                    emailSignature: undefined,
                    emailBodyClosing: undefined,
                    emailBodyIntroduction: undefined,
                },
                quoteReminderTemplateErrors: {
                    emailSubject: undefined,
                    emailSignature: undefined,
                    emailBodyClosing: undefined,
                    emailBodyIntroduction: undefined,
                },
            },
        });
        setFormData('step2', {
            ...formData.step2,
            [key]: event.target.value,
        });
    };

    const handleCcChange = (_event, value: string[]) => {
        const ev = { target: { value: value } };
        const key = formData.step2.isScreenTwo ? 'reminderContactsCC' : 'contactsCC';
        handleStateChange(ev, key);
    };

    const errorkey = 'quoteEmailTemplateError';

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '24px',
                maxHeight: 'calc(100vh - 168px)',
                overflow: 'auto',
            }}
            p={4}
        >
            <Box sx={quoteEmailSectionsStyle}>
                <Stack spacing={1}>
                    <Typography variant="h2">Quote email template</Typography>
                    <Typography variant="body2">
                        Customize your email template and see changes on preview on the right.
                    </Typography>
                </Stack>
                <Divider orientation="horizontal" />
                <Stack spacing={2}>
                    <Typography variant="body2">Enable quote approvals</Typography>
                    <LexoSwitch
                        checked={formData.step2.isQuoteApprovalOn}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const ev = { target: { value: event.target.checked } };
                            handleStateChange(ev, 'isQuoteApprovalOn');
                        }}
                    />
                    <Typography variant="body3">
                        If quote approval is enabled, you can make edits to the quote before sending
                        it to the client.
                        <br />
                        If disabled, clients will receive the quote immediately upon submission of
                        the quote form.
                    </Typography>
                    <Divider orientation="horizontal" />
                    {showTip && (
                        <Alert
                            icon={<InfoSolid />}
                            severity="info"
                            onClose={() => {
                                setShowTip(false);
                            }}
                            sx={{ bgcolor: '#F0F5FD' }}
                        >
                            <AlertTitle>Fee calculations</AlertTitle>
                            Please note that all sections of the email template are editable except
                            for the fee breakdown. The fee breakdown will be automatically
                            calculated by Lexo based on your fee model and estimations provided
                            earlier.
                        </Alert>
                    )}
                    <FormControl fullWidth>
                        <TextField
                            value={formData.step2.expiry}
                            onChange={(ev) => handleStateChange(ev, 'expiry')}
                            fullWidth
                            placeholder="Quote Request Response"
                            type="text"
                            variant="outlined"
                            label="Number of days before quote expires"
                            name="quote-expiry-count"
                            disabled
                        />

                        {showTip && (
                            <Alert
                                icon={<InfoSolid />}
                                severity="info"
                                onClose={() => {
                                    setShowTip(false);
                                }}
                                sx={{
                                    background: theme.components?.MuiAlert?.styleOverrides,
                                    mt: 2,
                                }}
                            >
                                <AlertTitle>Number of days</AlertTitle>
                                This field is currently not editable
                            </Alert>
                        )}
                    </FormControl>
                </Stack>
                <Stack spacing={4}>
                    <Box sx={formItemContainerStyle}>
                        <TextField
                            value={formData.step2.subject}
                            onChange={(ev) => handleStateChange(ev, 'subject')}
                            fullWidth
                            placeholder="Quote Request Response"
                            type="text"
                            variant="outlined"
                            label="Subject"
                            name="subject"
                            error={!!errorData.step2[errorkey]?.emailSubject}
                            helperText={errorData.step2[errorkey]?.emailSubject}
                        />
                    </Box>
                    <Box sx={formItemContainerStyle}>
                        <TextField
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: 'initial',
                                },
                            }}
                            value={formData.step2.bodyBeforePricing}
                            onChange={(ev) => handleStateChange(ev, 'bodyBeforePricing')}
                            name="bodyBeforePricing"
                            error={!!errorData.step2[errorkey]?.emailBodyIntroduction}
                            helperText={
                                errorData.step2[errorkey]?.emailBodyIntroduction
                                    ? 'Email body introduction is required.'
                                    : ''
                            }
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={3}
                            variant="outlined"
                            label="First part of body (before fee)"
                        />
                    </Box>
                    <Box sx={formItemContainerStyle}>
                        <TextField
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: 'initial',
                                },
                            }}
                            value={formData.step2.bodyAfterPricing}
                            onChange={(ev) => handleStateChange(ev, 'bodyAfterPricing')}
                            name="bodyAfterPricing"
                            error={!!errorData.step2[errorkey]?.emailBodyClosing}
                            helperText={
                                errorData.step2[errorkey]?.emailBodyClosing
                                    ? 'Email body closing is required.'
                                    : ''
                            }
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={3}
                            variant="outlined"
                            label="Second part of body (after fee)"
                        />
                    </Box>
                    <Box sx={{ ...formItemContainerStyle, flexDirection: 'column' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '4px',
                                marginBottom: '16px',
                            }}
                        >
                            <Typography variant="labelMedium" color={theme.palette.neutral.black}>
                                Contacts to CC
                            </Typography>
                            <Tooltip
                                arrow
                                placement="top-start"
                                title={
                                    <Box>
                                        <Typography
                                            variant="h4"
                                            sx={{ color: theme.palette.neutral.black }}
                                        >
                                            Users included in this section will be CC'ed on the
                                            following notifications:
                                        </Typography>
                                        <br />
                                        <Typography variant="body2">
                                            <ul>
                                                <li>Lawyer approval notification</li>
                                                <li>Quote accepted notification</li>
                                                <li>Call requested notification</li>
                                                <li>Quote declined notifications</li>
                                            </ul>
                                        </Typography>
                                    </Box>
                                }
                            >
                                <IconButton sx={{ padding: '0px' }}>
                                    <InfoIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <AutoCompleteMultiSelect
                            fullWidth
                            getOptionLabel={(options) => options}
                            label=""
                            value={formData.step2.contactsCC}
                            options={availableCcContacts}
                            onChange={handleCcChange}
                        />
                    </Box>
                    <Box sx={formItemContainerStyle}>
                        <TextField
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: 'initial',
                                },
                            }}
                            value={formData.step2.disclaimerText}
                            onChange={(ev) => handleStateChange(ev, 'disclaimerText')}
                            name="disclaimerText"
                            fullWidth
                            placeholder=""
                            type="text"
                            multiline={true}
                            rows={3}
                            variant="outlined"
                            label="Disclaimer text"
                        />
                    </Box>
                </Stack>
            </Box>
            <Box sx={quoteEmailSectionsStyle}>
                <Stack spacing={1}>
                    <Typography variant="h2">Template preview</Typography>
                    <Typography variant="body2">
                        This is a preview of the email that your clients will see in their inbox.
                    </Typography>
                </Stack>
                <Divider orientation="horizontal" />
                <Box
                    sx={{
                        borderRadius: '4px',
                        border: '1px solid #E0E3E5',
                        background: '#F0F5FD',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        padding: '16px 0',
                        '& > *': {
                            padding: '16px',
                        },
                        wordWrap: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    <Typography
                        variant="h3"
                        className={hasSubjectChanged ? 'activity-tracker' : ''}
                    >
                        {formData.step2.subject || 'Quote Request Response'}
                    </Typography>
                    <Typography variant="body1" sx={{ marginTop: '8px' }}>
                        Hello [client name],
                    </Typography>
                    <Typography
                        variant="body1"
                        className={hasBodyBeforePricingChanged ? 'activity-tracker' : ''}
                    >
                        {formData.step2.bodyBeforePricing && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: convertToHtml(formData.step2.bodyBeforePricing),
                                }}
                            ></span>
                        )}
                    </Typography>
                    <DynamicQuoteStructure />
                    <Typography
                        variant="body1"
                        className={hasBodyAfterPricingChanged ? 'activity-tracker' : ''}
                    >
                        {formData.step2.bodyAfterPricing && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: convertToHtml(formData.step2?.bodyAfterPricing),
                                }}
                            ></span>
                        )}
                    </Typography>
                    <Stack spacing={2}>
                        <Typography variant="h3" color="#16171A">
                            Select one of the options below to respond
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" sx={{ textTransform: 'none' }} disabled>
                                Accept
                            </Button>
                            <Button variant="outlined" sx={{ textTransform: 'none' }} disabled>
                                Request a call
                            </Button>
                            <Button variant="outlined" sx={{ textTransform: 'none' }} disabled>
                                Decline
                            </Button>
                        </Stack>
                    </Stack>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '12px',
                            color: '#555',
                            fontStyle: 'italic',
                            lineHeight: 1.5,
                            mt: 1.5,
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {formData.step2.disclaimerText && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: convertToHtml(formData.step2.disclaimerText),
                                }}
                            />
                        )}
                    </Typography>
                    <Typography
                        variant="body1"
                        className={hasSignatureChanged ? 'activity-tracker' : ''}
                    >
                        {formData.step2.signatureOfEmail && (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: convertToHtml(formData.step2.signatureOfEmail),
                                }}
                            ></span>
                        )}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
